import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import { cn } from '../../utils/cn';

interface ResetPasswordData {
  newPassword: string;
  confirmPassword: string;
}

interface ResetPasswordResponse {
  success: boolean;
  data: unknown;
  expandedError?: {
    errorCode: number;
    message: string;
    technicalMessage: string | null;
  };
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ResetPasswordData>({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [linkInvalid, setLinkInvalid] = useState<boolean>(false);
  const [linkReady, setLinkReady] = useState<boolean>(false);

  useEffect(() => {
    const emailParam = searchParams.get('Email');
    const tokenParam = searchParams.get('token');

    if (!emailParam || !tokenParam) {
      setLinkInvalid(true);
      setLinkReady(true);
      return;
    }

    const fixedToken = tokenParam.replace(/ /g, '+');
    setEmail(emailParam);
    setToken(fixedToken);
    setLinkInvalid(false);
    setLinkReady(true);
  }, [searchParams]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Minimum 8 characters');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('At least one lowercase letter');
    }

    if (!/\d/.test(password)) {
      errors.push('At least one digit');
    }

    if (!/[^\w\s]/.test(password)) {
      errors.push('At least one special character');
    }

    return errors;
  };

  const handleInputChange = (field: keyof ResetPasswordData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.newPassword) {
        setError('Please enter a new password');
        return;
      }

      if (!formData.confirmPassword) {
        setError('Please confirm your new password');
        return;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setError('New password and confirm password do not match');
        return;
      }

      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        setError(`Password does not meet the required criteria: ${passwordErrors.join(', ')}`);
        return;
      }

      if (!email || !token) {
        setError('Invalid reset password link. Please request a new password reset.');
        return;
      }

      const requestPayload = {
        email,
        token,
        newPassword: formData.newPassword
      };

      const response = await fetch(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0' || !response.body) {
          setSuccess('Password reset successfully! You can now log in with your new password.');
          setFormData({ newPassword: '', confirmPassword: '' });
        } else {
          try {
            const data: ResetPasswordResponse = await response.json();
            if (data.success) {
              setSuccess('Password reset successfully! You can now log in with your new password.');
              setFormData({ newPassword: '', confirmPassword: '' });
            } else {
              setError(data.expandedError?.message || 'Failed to reset password');
            }
          } catch {
            setSuccess('Password reset successfully! You can now log in with your new password.');
            setFormData({ newPassword: '', confirmPassword: '' });
          }
        }
      } else {
        try {
          const data: ResetPasswordResponse = await response.json();
          setError(data.expandedError?.message || 'Failed to reset password');
        } catch {
          setError(`Failed to reset password (${response.status})`);
        }
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('An error occurred while resetting password');
    } finally {
      setLoading(false);
    }
  };

  const passwordErrors = formData.newPassword ? validatePassword(formData.newPassword) : [];

  const inputClass = cn(
    'w-full rounded-xl border-0 bg-gray-100 px-4 py-3 text-slate-900',
    'placeholder:text-slate-400',
    'focus:outline-none focus:ring-2 focus:ring-sky-500/30'
  );

  if (!linkReady) {
    return (
      <div
        className={cn(
          'min-h-screen homeBg',
          'bg-gradient-to-b from-gray-200 to-gray-900',
          'flex flex-col items-center justify-center',
          'px-4 py-10'
        )}
      >
        <div
          className="h-9 w-9 animate-spin rounded-full border-2 border-sky-500 border-t-transparent"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  const marketingSection = (
    <section
      className={cn(
        'flex flex-1 flex-col rounded-3xl bg-white/95 p-8 shadow-sm backdrop-blur-sm sm:p-10 lg:max-w-xl lg:p-12 xl:p-16 blurLayerInteractive'
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
        Business payment workspace
      </p>
      <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
        Make complex payment operations feel obvious from the first click.
      </h1>
      <p className="mt-4 text-base leading-relaxed text-slate-500">
        ZK Gate is built for online businesses that need modern payment flow, clean settlement, and a calmer operational experience for the people running it every day.
      </p>
      <div className="mt-8 rounded-2xl bg-slate-100 p-6 sm:p-8">
        <h2 className="text-sm font-semibold text-slate-900">Choosing a strong password</h2>
        <ul className="mt-4 flex flex-col gap-3">
          {[
            'Use at least eight characters with mixed case, numbers, and symbols.',
            'Avoid reusing passwords from other services or older ZK Gate credentials.',
            'After you reset, sign in from the main login page with your updated password.',
          ].map((item) => (
            <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );

  if (linkInvalid) {
    return (
      <div
        className={cn(
          'min-h-screen homeBg',
          'bg-gradient-to-b from-gray-200 to-gray-900',
          'flex flex-col justify-center',
          'px-4 py-10 sm:px-6 sm:py-12 lg:px-8'
        )}
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 lg:flex-row lg:items-stretch lg:justify-center">
          {marketingSection}
          <section
            className={cn(
              'flex w-full flex-col rounded-3xl bg-white/95 p-8 shadow-sm backdrop-blur-sm sm:p-10 lg:max-w-md lg:flex-none lg:p-12 xl:p-16'
            )}
          >
            <header>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Secure workspace</p>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Link not valid</h2>
              <p className="mt-2 text-sm text-slate-500">
                This reset link is missing required details or has expired. Request a new email to continue.
              </p>
            </header>
            <div
              role="alert"
              className="mt-8 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              Invalid reset password link. Please request a new password reset.
            </div>
            <footer className="mt-8 flex flex-col items-center gap-3">
              <Link
                to="/forgot-password"
                className={cn(
                  'inline-flex w-full items-center justify-center rounded-xl bg-sky-500 py-3.5 text-base font-semibold text-white',
                  'shadow-md shadow-sky-500/25 transition hover:bg-sky-600',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2'
                )}
              >
                Request new reset
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-sky-600 hover:text-sky-700 focus:outline-none focus-visible:underline"
              >
                Back to login
              </Link>
            </footer>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'min-h-screen homeBg',
        'bg-gradient-to-b from-gray-200 to-gray-900',
        'flex flex-col justify-center',
        'px-4 py-10 sm:px-6 sm:py-12 lg:px-8'
      )}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 lg:flex-row lg:items-stretch lg:justify-center">
        {marketingSection}

        <section
          className={cn(
            'flex w-full flex-col rounded-3xl bg-white/95 p-8 shadow-sm backdrop-blur-sm sm:p-10 lg:max-w-md lg:flex-none lg:p-12 xl:p-16'
          )}
        >
          <header>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Secure workspace</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Set new password</h2>
            <p className="mt-2 text-sm text-slate-500">
              Choose a strong password for <span className="font-semibold text-slate-700">{email}</span>.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            {error && (
              <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            {success && (
              <div role="status" className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                {success}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="reset-new-password" className="text-sm font-bold text-slate-700">
                New password
              </label>
              <div className="relative">
                <input
                  id="reset-new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Enter new password"
                  required
                  className={cn(inputClass, 'pr-16')}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2',
                    'text-sm font-medium text-sky-600',
                    'rounded-lg px-1 py-0.5',
                    'hover:text-sky-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40'
                  )}
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {formData.newPassword ? (
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="text-xs font-medium text-slate-500">Password requirements</p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {[
                      { key: 'Minimum 8 characters', ok: !passwordErrors.includes('Minimum 8 characters') },
                      { key: 'At least one uppercase letter', ok: !passwordErrors.includes('At least one uppercase letter') },
                      { key: 'At least one lowercase letter', ok: !passwordErrors.includes('At least one lowercase letter') },
                      { key: 'At least one digit', ok: !passwordErrors.includes('At least one digit') },
                      { key: 'At least one special character', ok: !passwordErrors.includes('At least one special character') },
                    ].map(({ key, ok }) => (
                      <li key={key} className={cn(ok ? 'text-emerald-700' : 'text-red-600')}>
                        {key}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="reset-confirm-password" className="text-sm font-bold text-slate-700">
                Confirm new password
              </label>
              <div className="relative">
                <input
                  id="reset-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className={cn(inputClass, 'pr-16')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2',
                    'text-sm font-medium text-sky-600',
                    'rounded-lg px-1 py-0.5',
                    'hover:text-sky-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40'
                  )}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? (
                <p className="text-xs text-red-600">Passwords do not match</p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={
                loading ||
                !formData.newPassword ||
                !formData.confirmPassword ||
                passwordErrors.length > 0
              }
              className={cn(
                'w-full rounded-xl bg-sky-500 py-3.5 text-base font-semibold text-white',
                'shadow-md shadow-sky-500/25',
                'transition hover:bg-sky-600 hover:shadow-sky-500/35',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                'inline-flex items-center justify-center gap-2'
              )}
            >
              {loading ? (
                <>
                  <span
                    className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                    aria-hidden
                  />
                  <span>Resetting…</span>
                </>
              ) : (
                'Reset password'
              )}
            </button>
          </form>

          <footer className="mt-8 flex justify-center">
            <Link
              to="/login"
              className="text-sm font-medium text-sky-600 hover:text-sky-700 focus:outline-none focus-visible:underline"
            >
              Back to login
            </Link>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default ResetPassword;
